import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-new-project-property-features',
  templateUrl: './new-project-property-features.component.html',
  styleUrls: ['./new-project-property-features.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule]
})
export class NewProjectPropertyFeaturesComponent {
  @Input() formGroup!: FormGroup | null;
  @Output() fieldChange = new EventEmitter<any>();

  // Variables para características del inmueble
  showFieldSelector: boolean = false;

  // Campos disponibles para selección dinámica
  readonly camposDisponibles = [
    { id: 'planta', nombre: 'Planta', tipo: 'numerico', icono: '🏢' },
    { id: 'habitaciones', nombre: 'Habitaciones', tipo: 'numerico', icono: '🛏️' },
    { id: 'banos', nombre: 'Baños', tipo: 'numerico', icono: '🚿' },
    { id: 'exterior', nombre: 'Exterior', tipo: 'si_no', icono: '☀️' },
    { id: 'ascensor', nombre: 'Ascensor', tipo: 'si_no', icono: '🛗' },
    { id: 'metros_cuadrados', nombre: 'M²', tipo: 'numerico', icono: '📏' },
    { id: 'atico', nombre: 'Ático', tipo: 'si_no', icono: '🏠' },
    { id: 'duplex', nombre: 'Dúplex', tipo: 'si_no', icono: '🏘️' },
    { id: 'edificio', nombre: 'Edificio', tipo: 'si_no', icono: '🏢' },
    { id: 'piscina', nombre: 'Piscina', tipo: 'si_no', icono: '🏊' },
    { id: 'fachada_senorial', nombre: 'Fachada señorial', tipo: 'si_no', icono: '🏛️' }
  ];

  // Campos seleccionados actualmente (máximo 6)
  camposSeleccionados: string[] = ['planta', 'habitaciones', 'banos', 'exterior', 'ascensor', 'metros_cuadrados'];

  constructor() { }

  // Métodos para manejar características del inmueble
  toggleFieldSelector() {
    this.showFieldSelector = !this.showFieldSelector;
  }

  closeFieldSelector(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showFieldSelector = false;
  }

  toggleCampoSeleccionado(campoId: string): void {
    const index = this.camposSeleccionados.indexOf(campoId);
    
    if (index > -1) {
      // Si está seleccionado, lo removemos
      this.camposSeleccionados.splice(index, 1);
    } else {
      // Si no está seleccionado y no hemos alcanzado el máximo
      if (this.camposSeleccionados.length < 6) {
        this.camposSeleccionados.push(campoId);
      }
    }
  }

  isCampoSeleccionado(campoId: string): boolean {
    return this.camposSeleccionados.includes(campoId);
  }

  getCamposSeleccionados() {
    return this.camposDisponibles.filter(campo => 
      this.camposSeleccionados.includes(campo.id)
    );
  }

  getCampoControl(campoId: string): FormControl | null {
    // Mapear los IDs a los nombres de los controles del formulario
    const controlMap: { [key: string]: string } = {
      'planta': 'floor',
      'habitaciones': 'rooms',
      'banos': 'bathrooms',
      'exterior': 'exterior',
      'ascensor': 'elevator',
      'metros_cuadrados': 'squareMeters',
      'atico': 'penthouse',
      'duplex': 'duplex',
      'edificio': 'building',
      'piscina': 'pool',
      'fachada_senorial': 'nobleFacade'
    };

    const controlName = controlMap[campoId];
    if (!this.formGroup) {
      console.warn(`[PropertyFeatures] FormGroup no disponible para campo: ${campoId}`);
      return null;
    }
    
    const control = this.formGroup.get(controlName) as FormControl | null;
    if (!control && controlName) {
      console.warn(`[PropertyFeatures] Control '${controlName}' no encontrado en FormGroup para campo: ${campoId}`);
    }
    return control;
  }

  confirmFieldSelection() {
    this.showFieldSelector = false;
  }

  // Métodos para manejar cambios en inputs numéricos
  onNumberInput(event: Event, campoId: string): void {
    const input = event.target as HTMLInputElement;
    this.updateNumberControl(input, campoId);
  }

  onNumberChange(event: Event, campoId: string): void {
    const input = event.target as HTMLInputElement;
    this.updateNumberControl(input, campoId);
  }

  onNumberBlur(event: Event, campoId: string): void {
    const input = event.target as HTMLInputElement;
    this.updateNumberControl(input, campoId);
  }

  onNumberKeyup(event: KeyboardEvent, campoId: string): void {
    const input = event.target as HTMLInputElement;
    // Si se presionan las flechas arriba/abajo, actualizar el control
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      this.updateNumberControl(input, campoId);
    }
  }

  private updateNumberControl(input: HTMLInputElement, campoId: string): void {
    const control = this.getCampoControl(campoId);
    
    if (!control) {
      return;
    }
    
    if (input.value === '' || input.value === null || input.value === undefined) {
      control.setValue(null, { emitEvent: true, onlySelf: false });
    } else {
      const value = parseInt(input.value, 10);
      if (!isNaN(value) && value >= 0) {
        control.setValue(value, { emitEvent: true, onlySelf: false });
        // Sincronizar el valor del input con el control
        if (input.value !== value.toString()) {
          input.value = value.toString();
        }
      } else {
        control.setValue(null, { emitEvent: true, onlySelf: false });
        input.value = '';
      }
    }
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: true });
  }
}
